//* External Libraries
import Container from '@material-ui/core/Container'
import React, { useEffect, useRef, useState } from 'react'

//* Styles
import { Grid, GridWrapper, PaginationContainer, PartnersHeader, Wrapper } from '../partnerListings'

//* Components
import Breadcrumbs from '../../../global/breadcrumbs'
import GoTop from '../../../global/goTop'
import Pagination from '../../../global/pagination'
import SEOTags from '../../../global/seoTags'
import Layout from '../../../layouts/layout'
import PartnersModal from '../partnersModal'
import BadgeDetails from './subcomponents/badgeDetails'
import BecomeAPartner from './subcomponents/becomeAPartner'
import { Cards, EmptyResults } from './subcomponents/cards'
import Filters from './subcomponents/filters'
import Search from './subcomponents/search'

//* Images
import btnPrimaryIcon from '../../../../../static/steam-img/arrows/button-primary-arrow-icon.svg'

//* Helpers
import {
  getBusinessPartnersTiersMicrocopy,
  getPartnersTabMicrocopies,
} from '../../../../utils/partners.utils'
import UrlUtil from '../../../../utils/url.utils'

//* Constants
import { ModuleNames } from '../../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../../constants/localization.constants'
import { FIELDS, PATH } from '../../../../constants/partners'

const BusinessPartners = ({
  pageContext: { businessPartners, businessPartnersLocations, pageData },
  path,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}: any) => {

  if (!pageData?.contentfulPageFlex) return null
  const { name, modules, seoMetadata } = pageData?.contentfulPageFlex
  const locationData = path || '/'
  const pageName = name || 'Business Partners'

  const partnersListingNameTypes =
    modules && modules.length >= 2 && modules[1].key == 'Partners Listing' ? modules[1].values : []

  const {
    businessPartnersTabText,
    businessPartnersTabUrl,
    publicSectorPartnersTabText,
    publicSectorPartnersTabUrl,
    technologyPartnersTabText,
    technologyPartnersTabUrl,
    servicePartnersTabText,
    servicePartnersTabUrl,
  } = getPartnersTabMicrocopies(partnersListingNameTypes)

  const {
    platinumTitleText,
    platinumBodyText,
    diamondTitleText,
    diamondBodyText,
    goldTitleText,
    goldBodyText,
    authorizedTitleText,
    authorizedBodyText,
    certifiedTitleText,
    certifiedBodyText,
    awardTitleText,
    awardBodyText,
  } = getBusinessPartnersTiersMicrocopy(partnersListingNameTypes)

  const [businessPartnersData, setBusinessPartnerData] = useState([])

  //* tempRendering
  const [toggleReRender, setToggleReRender] = useState(false)

  //* Search
  const [showSearch, setShowSearch] = useState(false)
  const [searchText, setSearchText] = useState('')
  const searchInputRef: any = useRef<HTMLInputElement>()

  //* Filters
  const [isLocationFilterActive, setIsLocationFilterActive] = useState(false)
  const [locationFilter, setLocationFilter] = useState('')
  const [isTypeFilterActive, setIsTypeFilterActive] = useState(false)
  const [typeFilter, setTypeFilter] = useState('')
  const [isCertificationsFilterActive, setIsCertificationsFilterActive] = useState(false)
  const [certificationsFilter, setCertificationsFilter] = useState('')

  //* Pagination
  const docsPerPage = 12

  //* Popup box
  const [open, setOpen] = useState(false) //modal
  const [selectedPartner, setSelectedPartner] = useState() //modal data

  const handleShowSearch = (e: any) => {
    setShowSearch(true)
  }

  const handleCloseSearch = (e: any) => {
    setSearchText('')
    setShowSearch(false)

    if (searchText) {
      handlePaginationClick(1)
      history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'search'))
    }
  }

  const handleSearchInput = (e: any) => {
    const value = e.target.value.toLowerCase().split('/').join('_').replace(/ /g, '-')
    setSearchText(e.target.value)

    handlePaginationClick(1)
    history.pushState(null, '', UrlUtil.updateURLParameter(window.location.href, 'search', value))
  }

  const handleFilterClick = (e: any) => {
    const parentId = e.target.parentElement.id
    e.preventDefault()

    if (e.target.id === 'location' || parentId === 'location') {
      setIsLocationFilterActive(!isLocationFilterActive)
      setIsTypeFilterActive(false)
      setIsCertificationsFilterActive(false)
    } else if (e.target.id === 'type' || parentId === 'type') {
      setIsTypeFilterActive(!isTypeFilterActive)
      setIsLocationFilterActive(false)
      setIsCertificationsFilterActive(false)
    } else if (e.target.id === 'certifications' || parentId === 'certifications') {
      setIsCertificationsFilterActive(!isCertificationsFilterActive)
      setIsLocationFilterActive(false)
      setIsTypeFilterActive(false)
    }
  }

  const handleFilterItemClick = (e: any) => {
    e.preventDefault()

    if (e.target.getAttribute('data-item') === 'location') {
      const value = e.target.textContent.toLowerCase().replace(/ /g, '-')
      history.pushState(
        null,
        '',
        UrlUtil.updateURLParameter(window.location.href, 'location', value)
      )

      if (e.target.textContent.toLowerCase() === 'show all') {
        setLocationFilter('')
        history.pushState(
          null,
          '',
          UrlUtil.updateURLParameter(window.location.href, 'location', 'all')
        )
      } else {
        setLocationFilter(e.target.textContent)
      }

      setIsLocationFilterActive(false)
    } else if (e.target.getAttribute('data-item') === 'type') {
      const value = e.target.textContent.toLowerCase().replace(/ /g, '-')
      history.pushState(null, '', UrlUtil.updateURLParameter(window.location.href, 'type', value))

      if (e.target.textContent.toLowerCase() === 'show all') {
        setTypeFilter('')
        history.pushState(null, '', UrlUtil.updateURLParameter(window.location.href, 'type', 'all'))
      } else {
        setTypeFilter(e.target.textContent)
      }

      setIsTypeFilterActive(false)
    } else if (e.target.getAttribute('data-item') === 'certifications') {
      const value = e.target.textContent.toLowerCase().replace(/ /g, '-')

      history.pushState(
        null,
        '',
        UrlUtil.updateURLParameter(window.location.href, 'certification', value)
      )

      if (e.target.textContent.toLowerCase() === 'show all') {
        setCertificationsFilter('')
        history.pushState(
          null,
          '',
          UrlUtil.updateURLParameter(window.location.href, 'certification', 'all')
        )
      } else {
        setCertificationsFilter(e.target.textContent)
      }

      setIsCertificationsFilterActive(false)
    }

    handlePaginationClick(1)
  }

  const handleResetFilters = () => {
    setLocationFilter('')
    setTypeFilter('')
    setCertificationsFilter('')
    history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'location'))
    history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'type'))
    history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'certification'))
    handlePaginationClick(1)
  }

  const hrefForPage = (page: number) => {
    let url = ''

    if (page === 1) {
      //* url = /partners/business-partners
      if (typeof window !== 'undefined') {
        url = `${PATH.BUSINESS}${window.location.search}`
      } else if (pageData && pageData.path) {
        url = `${PATH.BUSINESS}`
      }
    } else {
      //* url = /partners/business-partners/page?[query]
      if (typeof window !== 'undefined') {
        url = `${PATH.BUSINESS}/${page}${window.location.search}`
      } else if (pageData && pageData.path) {
        url = `${PATH.BUSINESS}/${page}`
      }
    }

    return url
  }

  //* only control the current page in one location NO STATE
  const getPageNumber = () => {
    if (typeof window !== 'undefined') {
      let page = UrlUtil.getPageNumberForPagniation(window.location.pathname)
      if (page && page !== '') {
        return parseInt(page)
      }
    } else {
      if (pageData && pageData.path) {
        let page = UrlUtil.getPageNumberForPagniation(pageData.path)
        if (page) {
          return parseInt(page)
        }
      }
    }

    return 1
  }

  //* only used internally not by the paging system
  const handlePaginationClick = (page: number) => {
    let urlQuery = typeof window !== 'undefined' ? window.location.search : ''

    if (page > 1) {
      history.pushState(null, '', `${PATH.BUSINESS}/${page}${urlQuery}`)
    } else {
      history.pushState(null, '', `${PATH.BUSINESS}${urlQuery}`)
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleClose = () => {
    setOpen(false)
  }

  const openModal = (partner: any) => {
    setSelectedPartner(partner)
    setOpen(true)
  }

  const handleTileClick = (event: any, partner: any) => {
    event.preventDefault()
    openModal(partner)
  }

  const init = () => {
    setBusinessPartnerData(businessPartners)
    if (
      UrlUtil.getUrlParameter('location') ||
      UrlUtil.getUrlParameter('type') ||
      UrlUtil.getUrlParameter('certification')
    ) {
      setLocationFilter(
        UrlUtil.getUrlParameter('location') !== 'all'
          ? UrlUtil.getUrlParameter('location').replace(/-/g, ' ')
          : ''
      )

      setTypeFilter(
        UrlUtil.getUrlParameter('type') !== 'all'
          ? UrlUtil.getUrlParameter('type').replace(/-/g, ' ')
          : ''
      )

      setCertificationsFilter(
        UrlUtil.getUrlParameter('certification') !== 'all'
          ? UrlUtil.getUrlParameter('certification').replace(/-/g, ' ')
          : ''
      )
    }

    if (UrlUtil.getUrlParameter('search')) {
      //* If there's a search, populate the search bar with existing data
      setSearchText(
        UrlUtil.getUrlParameter('search') !== ''
          ? UrlUtil.getUrlParameter('search').replace(/-/g, ' ')
          : ''
      )

      setShowSearch(true)
    }

    setToggleReRender(true)
  }

  const filterPartners = () => {
    //* this means we haven't run init yet (like when doing the html rendering during createPage rendering)
    if (!businessPartnersData || businessPartnersData.length == 0) {
      setLocationFilter('')
      setTypeFilter('')
      setCertificationsFilter('')
      setSearchText('')
      setBusinessPartnerData(businessPartners)
    }

    let partnerData = businessPartnersData
      .filter((partner: any) => {
        const businessLevel = partner[FIELDS.BUSINESS_LEVEL]
        const businessContract = partner[FIELDS.BUSINESS_CONTRACT]

        //! Partner must have a level and contract to be displayed
        if (businessLevel && businessContract) {
          return true
        }
      })
      .filter((partner: any) => {
        if (!locationFilter) {
          return true
        } else if (partner[FIELDS.COUNTRY].toLowerCase() === locationFilter.toLowerCase()) {
          return true
        }
      })
      .filter((partner: any) => {
        if (!typeFilter) {
          return true
        } else if (
          partner[FIELDS.BUSINESS_LEVEL] &&
          partner[FIELDS.BUSINESS_LEVEL].toLowerCase() === typeFilter.toLowerCase()
        ) {
          return true
        }
      })
      .filter((partner: any) => {
        if (!certificationsFilter) {
          return true
        } else if (
          partner[FIELDS.CERTIFICATION] &&
          partner[FIELDS.CERTIFICATION].toLowerCase().includes(certificationsFilter.toLowerCase())
        ) {
          return true
        }
      })
      .filter((partner: any) => {
        if (!searchText) {
          return true
        } else if (
          (partner[FIELDS.COUNTRY] &&
            partner[FIELDS.COUNTRY].toLowerCase().includes(searchText.toLowerCase())) ||
          (partner[FIELDS.NAME] &&
            partner[FIELDS.NAME].toLowerCase().includes(searchText.toLowerCase())) ||
          (partner[FIELDS.DESCRIPTION] &&
            partner[FIELDS.DESCRIPTION].toLowerCase().includes(searchText.toLowerCase()))
        ) {
          return true
        }
      })

    return partnerData
  }

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    if (searchInputRef && searchInputRef.current) searchInputRef.current.focus()
  }, [showSearch])

  const generateDynamicHero = () => {
    let headline, subheadline

    for (let i = 0; i < modules?.length; i++) {
      if (modules[i].__typename && modules[i].__typename === ModuleNames.HERO) {
        const hero = modules[i]

        if (hero.headline) {
          headline = hero.headline
        }

        if (hero.subheadline) {
          subheadline = hero.subheadline?.subheadline || hero.subheadline
        }
      }
    }

    return [headline, subheadline]
  }

  const dynamicHeroData = generateDynamicHero()
  const seoData = seoMetadata || null
  const currentPage = getPageNumber()
  const start = (currentPage - 1) * docsPerPage
  const end = currentPage * docsPerPage

  //* USN Filter certifications
  let filterCertificationTypes: any = new Set()

  businessPartners.forEach((record: any) => {
    if (record.Partner_Locator_Certifications__c) {
      let allCertifications = record.Partner_Locator_Certifications__c.split(';')
      allCertifications.forEach((cert: any) => filterCertificationTypes.add(cert))
    }
  })

  filterCertificationTypes = Array.from(filterCertificationTypes).filter(
    certification => certification !== null
  )

  filterCertificationTypes = ['Show All', ...filterCertificationTypes]

  return (
    <Layout.Template
      lang={LocaleCodes.ENGLISH.code}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      slug={pageData.contentfulPageFlex.slug}>
      <div data-cy="BusinessPartnerPage">
        <SEOTags
          seoMetadata={seoData}
          slug={pageData.contentfulPageFlex.slug}
          path={path}
          isPaginated={true}
          pageNum={currentPage}
          hrefForPage={hrefForPage}
          isLastPage={currentPage === Math.ceil(filterPartners().length / docsPerPage)}
        />

        <Wrapper>
          <Grid></Grid>
          <Container>
            <Breadcrumbs
              backgroundTheme="light"
              currentPathname={locationData}
              pageName={pageName}
            />
            <PartnersHeader>
              <h1>{dynamicHeroData[0]}</h1>
              <p>{dynamicHeroData[1]}</p>
            </PartnersHeader>
          </Container>

          <Container>
            {/* Search Partners */}
            <Search
              showSearch={showSearch}
              handleShowSearch={handleShowSearch}
              searchText={searchText}
              handleSearchInput={handleSearchInput}
              searchInputRef={searchInputRef}
              handleCloseSearch={handleCloseSearch}
              businessPartnersTabUrl={businessPartnersTabUrl}
              businessPartnersTabText={businessPartnersTabText}
              publicSectorPartnersTabUrl={publicSectorPartnersTabUrl}
              publicSectorPartnersTabText={publicSectorPartnersTabText}
              technologyPartnersTabUrl={technologyPartnersTabUrl}
              technologyPartnersTabText={technologyPartnersTabText}
              servicePartnersTabUrl={servicePartnersTabUrl}
              servicePartnersTabText={servicePartnersTabText}
            />
          </Container>

          <Container>
            <div className="PartnersLocator__wrapper">
              {/* Partners List */}
              <div className="PartnersLocator__itemsContainer">
                <GridWrapper>
                  {filterPartners()
                    .slice(start, end)
                    .map((partner: any, i: number) => (
                      <Cards
                        partner={partner}
                        handleTileClick={handleTileClick}
                        index={i}
                        key={i}
                      />
                    ))}

                  {/* Search no results */}
                  {filterPartners().length === 0 && <EmptyResults />}
                </GridWrapper>

                <PaginationContainer>
                  {filterPartners().length > docsPerPage && (
                    <Pagination
                      activePage={currentPage}
                      hrefForPage={hrefForPage}
                      handleClick={handlePaginationClick}
                      numResults={filterPartners().length}
                      numPagesBeforeEllipses={3}
                      numResultsPerPage={docsPerPage}
                    />
                  )}
                </PaginationContainer>
              </div>

              <Filters
                isLocationFilterActive={isLocationFilterActive}
                locationFilter={locationFilter}
                handleFilterClick={handleFilterClick}
                handleFilterItemClick={handleFilterItemClick}
                businessPartnersLocations={businessPartnersLocations}
                isTypeFilterActive={isTypeFilterActive}
                typeFilter={typeFilter}
                isCertificationsFilterActive={isCertificationsFilterActive}
                filterCertificationTypes={filterCertificationTypes}
                btnPrimaryIcon={btnPrimaryIcon}
                certificationsFilter={certificationsFilter}
                handleResetFilters={handleResetFilters}
                partnersListingNameTypes={partnersListingNameTypes}
              />
            </div>
          </Container>

          {/* Become a partner */}
          <BecomeAPartner
            btnPrimaryIcon={btnPrimaryIcon}
            partnersListingNameTypes={partnersListingNameTypes}
          />
        </Wrapper>

        <BadgeDetails
          platinumTitleText={platinumTitleText}
          platinumBodyText={platinumBodyText}
          diamondTitleText={diamondTitleText}
          diamondBodyText={diamondBodyText}
          goldTitleText={goldTitleText}
          goldBodyText={goldBodyText}
          authorizedTitleText={authorizedTitleText}
          authorizedBodyText={authorizedBodyText}
          certifiedTitleText={certifiedTitleText}
          certifiedBodyText={certifiedBodyText}
          awardTitleText={awardTitleText}
          awardBodyText={awardBodyText}
        />

        <PartnersModal onClose={handleClose} open={open} data={selectedPartner} />
        <GoTop />
      </div>
    </Layout.Template>
  )
}

export default BusinessPartners
